/**
 * OAuth2 PKCE browser flow utilities
 *
 * Implements the Authorization Code Flow with PKCE (Proof Key for Code Exchange)
 * as defined in RFC 7636. This allows browser-based SPAs to securely obtain tokens
 * without exposing a client_secret.
 *
 * Flow:
 * 1. generateCodeVerifier() → store in sessionStorage
 * 2. generateCodeChallenge(verifier) → include in authorize URL
 * 3. Redirect browser to /oauth2/authorize with code_challenge
 * 4. On callback, exchange code + code_verifier for tokens
 */

/** Configuration for building the authorize URL */
export interface OAuth2AuthorizeConfig {
  /** IDAAS REST base URL (e.g. "http://localhost:50051") */
  idaasBaseUrl: string;
  /** OAuth2 client_id registered in IDAAS */
  clientId: string;
  /** Exact registered Resource Server audience requested by this client. */
  resource: string;
  /** Redirect URI registered for this client */
  redirectUri: string;
  /** OAuth2 scopes (e.g. "openid offline_access") */
  scope: string;
  /** PKCE code_challenge (generated from code_verifier) */
  codeChallenge: string;
  /** Random state for CSRF protection */
  state: string;
  /** Optional: prompt parameter ("none", "login", "consent") */
  prompt?: string;
}

export type PkceMode = "redirect" | "popup" | "iframe";

export interface WebMessageAuthorizeConfig {
  idaasBaseUrl: string;
  clientId: string;
  /** Exact registered Resource Server audience requested by this client. */
  resource: string;
  redirectUri: string;
  scope: string;
  codeChallenge: string;
  state: string;
  origin: string;
  display: "popup" | "iframe";
}

const CODE_VERIFIER_RANDOM_BYTES = 48;
const MAX_RESOURCE_BYTES = 128;
const RESOURCE_PATTERN = /^[A-Za-z0-9._:/-]+$/;
const PKCE_STATE_PATTERN = /^[A-Za-z0-9_-]{43}$/;
// IDAAS mints authorization codes as `z_ac_` + 64 lowercase hex (256-bit
// CSPRNG; servers/idaas domain raw_token) — mirror that contract exactly.
const AUTHORIZATION_CODE_PATTERN = /^z_ac_[0-9a-f]{64}$/;

/**
 * Generate a cryptographically random code_verifier for PKCE.
 *
 * The code_verifier is a random string of 43-128 characters from the
 * unreserved characters set (RFC 7636 §4.1).
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(CODE_VERIFIER_RANDOM_BYTES);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}

/**
 * Generate a code_challenge from a code_verifier using S256 method.
 *
 * code_challenge = BASE64URL(SHA256(code_verifier))
 *
 * Uses the Web Crypto API (available in all modern browsers).
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(hash);
}

/**
 * Generate a random state parameter for CSRF protection.
 */
export function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}

/**
 * Build the full authorize URL for browser redirect.
 */
export function buildAuthorizeUrl(config: OAuth2AuthorizeConfig): string {
  validateResource(config.resource);
  const url = new URL("/oauth2/authorize", config.idaasBaseUrl);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("resource", config.resource);
  url.searchParams.set("redirect_uri", config.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", config.scope);
  url.searchParams.set("state", config.state);
  url.searchParams.set("code_challenge", config.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  if (config.prompt) {
    url.searchParams.set("prompt", config.prompt);
  }
  return url.toString();
}

/**
 * Build the `/oauth2/authorize` URL for `response_mode=web_message` (popup / iframe).
 * Spec: `idaas/23-hosted-login-integration.md` §2.2.
 */
export function buildWebMessageAuthorizeUrl(config: WebMessageAuthorizeConfig): string {
  validateResource(config.resource);
  const url = new URL("/oauth2/authorize", config.idaasBaseUrl);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("resource", config.resource);
  url.searchParams.set("redirect_uri", config.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("response_mode", "web_message");
  url.searchParams.set("display", config.display);
  url.searchParams.set("scope", config.scope);
  url.searchParams.set("state", config.state);
  url.searchParams.set("code_challenge", config.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("origin", config.origin);
  return url.toString();
}

function validateResource(value: unknown): asserts value is string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    new TextEncoder().encode(value).length > MAX_RESOURCE_BYTES ||
    !RESOURCE_PATTERN.test(value)
  ) {
    throw new Error("resource must be a bounded registered audience");
  }
}

/**
 * Parse OAuth2 callback parameters from the current URL.
 *
 * Extracts `code` and `state` from the URL query string. If the authorization
 * server redirected with an OAuth2 error response (RFC 6749 §4.1.2.1:
 * `error=...&error_description=...`), throws a typed `OAuth2CallbackError`
 * carrying the provider's error code and description so callers can surface
 * the real failure to the user instead of a misleading "no parameters"
 * message.
 *
 * Returns `null` when neither a code nor an error is present (e.g. the page
 * was loaded directly without an OAuth2 redirect).
 */
export function parseCallbackParams(): { code: string; state: string } | null {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error");
  if (error) {
    throw new OAuth2CallbackError(
      error,
      params.get("error_description") ?? undefined,
      params.get("error_uri") ?? undefined,
    );
  }
  const code = params.get("code");
  const state = params.get("state");
  if (code === null && state === null) {
    return null;
  }
  if (state === null || !PKCE_STATE_PATTERN.test(state)) {
    throw new OAuth2CallbackInputError("state");
  }
  if (code === null || !AUTHORIZATION_CODE_PATTERN.test(code)) {
    throw new OAuth2CallbackInputError("code");
  }
  return { code, state };
}

/** Error raised when callback query material violates the IDAAS OAuth2 contract. */
export class OAuth2CallbackInputError extends Error {
  readonly field: "code" | "state";

  constructor(field: "code" | "state") {
    super(`OAuth2 callback ${field} is invalid`);
    this.name = "OAuth2CallbackInputError";
    this.field = field;
  }
}

/** Error raised when the OAuth2 provider redirects back with `?error=...`. */
export class OAuth2CallbackError extends Error {
  readonly oauthError: string;
  readonly errorDescription?: string;
  readonly errorUri?: string;

  constructor(oauthError: string, errorDescription?: string, errorUri?: string) {
    const detail = errorDescription ? `${oauthError}: ${errorDescription}` : oauthError;
    super(detail);
    this.name = "OAuth2CallbackError";
    this.oauthError = oauthError;
    this.errorDescription = errorDescription;
    this.errorUri = errorUri;
  }
}

/** Session storage namespace and lifetime for one PKCE attempt. */
const PKCE_KEY_PREFIX = "zero_pkce_attempt:";
const PKCE_TTL_MS = 10 * 60 * 1000;
const MAX_PKCE_ATTEMPTS_PER_CLIENT = 8;
const MAX_PKCE_RECORD_BYTES = 4096;
const MAX_REDIRECT_PATH_BYTES = 2048;

export interface PkceAttemptNamespace {
  idaasBaseUrl: string;
  clientId: string;
  resource: string;
}

export interface PkceState {
  verifier: string;
  state: string;
  redirectPath?: string;
  mode: PkceMode;
}

export function storePkceState(
  namespace: PkceAttemptNamespace,
  verifier: string,
  state: string,
  redirectPath?: string,
  mode: PkceMode = "redirect",
): void {
  if (!/^[A-Za-z0-9_-]{64}$/.test(verifier)) {
    throw new Error("PKCE verifier is invalid");
  }
  if (!PKCE_STATE_PATTERN.test(state)) {
    throw new Error("PKCE state is invalid");
  }
  if (redirectPath !== undefined && encodedByteLength(redirectPath) > MAX_REDIRECT_PATH_BYTES) {
    throw new Error(`PKCE redirect path must be at most ${MAX_REDIRECT_PATH_BYTES} bytes`);
  }

  const prefix = pkceNamespacePrefix(namespace);
  prunePkceAttempts(prefix);
  sessionStorage.setItem(
    `${prefix}${state}`,
    JSON.stringify({ verifier, state, redirectPath, mode, createdAt: Date.now() }),
  );
}

export function retrievePkceState(namespace: PkceAttemptNamespace, expectedState: string): PkceState | null {
  if (!PKCE_STATE_PATTERN.test(expectedState)) {
    return null;
  }
  const key = `${pkceNamespacePrefix(namespace)}${expectedState}`;
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  if (!raw) {
    return null;
  }
  const parsed = parsePkceRecord(raw, expectedState, Date.now());
  return parsed === null
    ? null
    : { verifier: parsed.verifier, state: parsed.state, redirectPath: parsed.redirectPath, mode: parsed.mode };
}

export function clearPkceState(namespace: PkceAttemptNamespace, state: string): void {
  if (PKCE_STATE_PATTERN.test(state)) {
    sessionStorage.removeItem(`${pkceNamespacePrefix(namespace)}${state}`);
  }
}

interface StoredPkceState extends PkceState {
  createdAt: number;
}

function pkceNamespacePrefix(namespace: PkceAttemptNamespace): string {
  const provider = encodeURIComponent(new URL(namespace.idaasBaseUrl).href);
  const client = encodeURIComponent(namespace.clientId);
  const resource = encodeURIComponent(namespace.resource);
  return `${PKCE_KEY_PREFIX}${provider}:${client}:${resource}:`;
}

function prunePkceAttempts(prefix: string): void {
  const now = Date.now();
  const active: Array<{ key: string; createdAt: number }> = [];
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (!key?.startsWith(prefix)) continue;
    const raw = sessionStorage.getItem(key);
    const record = raw === null ? null : parsePkceRecord(raw, undefined, now);
    if (record === null || key !== `${prefix}${record.state}`) {
      sessionStorage.removeItem(key);
      continue;
    }
    active.push({ key, createdAt: record.createdAt });
  }

  active.sort((left, right) => left.createdAt - right.createdAt);
  const excess = active.length - MAX_PKCE_ATTEMPTS_PER_CLIENT + 1;
  for (let index = 0; index < excess; index += 1) {
    const attempt = active[index];
    if (attempt) sessionStorage.removeItem(attempt.key);
  }
}

function parsePkceRecord(raw: string, expectedState: string | undefined, now: number): StoredPkceState | null {
  if (encodedByteLength(raw) > MAX_PKCE_RECORD_BYTES) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    if (!("verifier" in parsed) || typeof parsed.verifier !== "string") return null;
    if (!/^[A-Za-z0-9_-]{64}$/.test(parsed.verifier)) return null;
    if (!("state" in parsed) || typeof parsed.state !== "string" || !PKCE_STATE_PATTERN.test(parsed.state)) return null;
    if (expectedState !== undefined && parsed.state !== expectedState) return null;
    if (!("createdAt" in parsed) || typeof parsed.createdAt !== "number" || !Number.isSafeInteger(parsed.createdAt)) {
      return null;
    }
    const createdAt = parsed.createdAt;
    const age = now - createdAt;
    if (age < 0 || age > PKCE_TTL_MS) return null;

    const redirectPath =
      "redirectPath" in parsed && typeof parsed.redirectPath === "string" ? parsed.redirectPath : undefined;
    if (redirectPath !== undefined && encodedByteLength(redirectPath) > MAX_REDIRECT_PATH_BYTES) return null;
    const mode =
      "mode" in parsed && (parsed.mode === "redirect" || parsed.mode === "popup" || parsed.mode === "iframe")
        ? parsed.mode
        : "redirect";
    return { verifier: parsed.verifier, state: parsed.state, redirectPath, mode, createdAt };
  } catch {
    return null;
  }
}

function encodedByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
